// src/pages/TrackCreation/Step1.tsx
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import activeRadio from "../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../assets/images/radio_inactive.png";
import { RouteParamsWithLanguage } from "../../../../types/screensTypes";
import Button from "../../../../components/ui/Button";
import { getWaypointById } from "../../../../api";
import RadioButton from "../../../../components/common/RadioButton";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { WaypointInfoProps } from "../../../../types/types";
import NumberInput from "../../../../components/common/NumberInput";
import { useWaypointChangeDetection } from "../../../../hooks/useWaypointChangeDetection";
import stepStyles from "../createStep.module.css";
import wp from "./WaypointEdit.module.css";

interface WaypointSettings {
    isInGarden: boolean | null;
    isInLoop: boolean | null;
    isStraightLine: boolean | null;
    startDistance: number | null;
    isReferenceStopForReturnLoop: boolean | null;
}

const radioClasses = {
    dot: wp.radioDot,
    dotOn: wp.radioDotOn,
};

const WaypointInfo: React.FC<WaypointInfoProps> = ({ navigateToNext }) => {
    const { wptId, language } = useParams<RouteParamsWithLanguage>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const initialSettings: WaypointSettings = {
        isInGarden: false,
        isInLoop: false,
        isStraightLine: false,
        startDistance: null,
        isReferenceStopForReturnLoop: false,
    };

    const {
        value: settings,
        setValue: setSettings,
        saveIfChanged,
        setOriginalValue,
    } = useWaypointChangeDetection<WaypointSettings>({
        wptId: wptId || "",
        language,
        initialValue: initialSettings,
    });

    const isInGarden = settings.isInGarden;
    const isInLoop = settings.isInLoop;
    const isStraightLine = settings.isStraightLine;
    const startDistance = settings.startDistance;
    const isReferenceStopForReturnLoopWpt = settings.isReferenceStopForReturnLoop;

    const setIsInGarden = (value: boolean | null) => setSettings({ ...settings, isInGarden: value });
    const setIsInLoop = (value: boolean | null) => setSettings({ ...settings, isInLoop: value });
    const setIsStraightLine = (value: boolean | null) => setSettings({ ...settings, isStraightLine: value });
    const setStartDistance = (value: number | null) => setSettings({ ...settings, startDistance: value });
    const setIsReferenceStopForReturnLoopWpt = (value: boolean | null) =>
        setSettings({ ...settings, isReferenceStopForReturnLoop: value });

    const getWaypoint = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !language) return;
        const waypointData = await getWaypointById({ token, id, language });

        let loadedSettings: WaypointSettings = { ...initialSettings };

        if (waypointData) {
            const waypointArray = waypointData;
            if (waypointArray && waypointArray.waypointTemporary) {
                let inLoop = waypointArray.waypointTemporary.inLoop;
                let isStraightLine = waypointArray.waypointTemporary.isStraightLine;
                let isRefStop = waypointArray.waypointTemporary.isReferenceStopForReturnLoop;
                let type = waypointArray.waypointTemporary.type;
                let inGarden = false;
                let startDistance = waypointArray.waypointTemporary.startDistance;

                if (inLoop == null) {
                    const wp = waypointArray.waypoint;
                    inLoop = wp?.inLoop;
                }
                if (isRefStop == null) {
                    const wp = waypointArray.waypoint;
                    isRefStop = wp?.isReferenceStopForReturnLoop;
                }
                if (type == null) {
                    const wp = waypointArray.waypoint;
                    type = wp?.type;
                    if (type === "GARDEN") {
                        inGarden = true;
                    }
                } else {
                    if (type === "GARDEN") {
                        inGarden = true;
                    }
                }

                if (isStraightLine == null) {
                    const wp = waypointArray.waypoint;
                    isStraightLine = wp?.isStraightLine;
                }

                loadedSettings = {
                    isInGarden: inGarden,
                    isInLoop: inLoop ?? false,
                    isReferenceStopForReturnLoop: isRefStop ?? false,
                    startDistance: startDistance,
                    isStraightLine: isStraightLine ?? false,
                };
            } else {
                const wp = waypointArray.waypoint;
                if (wp) {
                    const inLoop = wp.inLoop;
                    const isRefStop = wp.isReferenceStopForReturnLoop;
                    const type = wp.type;
                    const isGarden = type === "GARDEN";
                    const startDistance = wp.startDistance;
                    const isStraightLine = wp.isStraightLine;

                    loadedSettings = {
                        startDistance: startDistance,
                        isInGarden: isGarden,
                        isInLoop: inLoop ?? false,
                        isReferenceStopForReturnLoop: isRefStop ?? false,
                        isStraightLine: isStraightLine ?? false,
                    };
                }
            }
        }

        setSettings(loadedSettings);
        setOriginalValue(loadedSettings);
    };

    useEffect(() => {
        if (wptId && wptId !== "" && language) {
            getWaypoint(wptId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wptId, language]);

    const saveWaypoint = async (): Promise<void> => {
        let type = "FIXED";
        if (settings.isInGarden === true) {
            type = "GARDEN";
        }
        await saveIfChanged({
            type,
            isReferenceStopForReturnLoop: settings.isReferenceStopForReturnLoop ?? false,
            inLoop: settings.isInLoop ?? false,
            startDistance: settings.startDistance ? settings.startDistance : null,
            isStraightLine: settings.isStraightLine ?? false,
        });
        if (!wptId || !language) return;
        navigateToNext(wptId, language);
    };

    const yesNo = (checkedYes: boolean, onYes: () => void, onNo: () => void, checkedNo: boolean) => (
        <div className={wp.radios}>
            <RadioButton
                label={t("yes")}
                checked={checkedYes}
                onChange={onYes}
                activeImage={activeRadio}
                inactiveImage={inactiveRadio}
                center
                classes={radioClasses}
            />
            <RadioButton
                label={t("no")}
                checked={checkedNo}
                onChange={onNo}
                activeImage={activeRadio}
                inactiveImage={inactiveRadio}
                center
                classes={radioClasses}
            />
        </div>
    );

    return (
        <div className={`${stepStyles.editScreen} ${wp.screen}`}>
            <TitleSubtitle title={t("waypointSettings")} subtitle={""} layout="edit" />

            <div className={wp.block}>
                <h2 className={wp.question}>{t("isEndOfSection")}</h2>
                {yesNo(
                    isReferenceStopForReturnLoopWpt === true,
                    () => setIsReferenceStopForReturnLoopWpt(true),
                    () => setIsReferenceStopForReturnLoopWpt(false),
                    isReferenceStopForReturnLoopWpt === false,
                )}
            </div>

            <div className={wp.block}>
                <h2 className={wp.question}>{t("isInLoop")}</h2>
                {yesNo(
                    isInLoop === true,
                    () => setIsInLoop(true),
                    () => setIsInLoop(false),
                    isInLoop === false,
                )}
            </div>

            <div className={wp.block}>
                <h2 className={wp.question}>{t("isStraightLine")}</h2>
                {yesNo(
                    isStraightLine === true,
                    () => setIsStraightLine(true),
                    () => setIsStraightLine(false),
                    isStraightLine === false,
                )}
            </div>

            <div className={wp.block}>
                <h2 className={wp.question}>{t("isInGarden")}</h2>
                {yesNo(
                    isInGarden === true,
                    () => setIsInGarden(true),
                    () => setIsInGarden(false),
                    isInGarden === false,
                )}
            </div>

            <div className={wp.block}>
                <h2 className={wp.question}>{t("startingDistance")}</h2>
                <div className={wp.numWrap}>
                    <NumberInput
                        placeholder={t("selectNumber")}
                        value={startDistance ? startDistance : 20}
                        size={"medium"}
                        fullWidth
                        onChange={(e) => setStartDistance(e.target.value ? parseInt(e.target.value) : 0)}
                        min={10}
                    />
                </div>
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveWaypoint}
                />
            </div>
        </div>
    );
};

export default WaypointInfo;
